import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { FormLocale } from '@jlg-website/shared';
import { textFor } from './copy';
import { timeZoneLabel } from './time-zones';

dayjs.extend(utc);
dayjs.extend(timezone);

/** The single configured dayjs of this component — nothing here imports dayjs directly. */
const dateTime = dayjs;

export const DATE_FORMAT = 'YYYY-MM-DD';
export const TIME_FORMAT = 'h:mm A';
export const MONTH_FORMAT = 'YYYY-MM';

const INTL_LOCALES: Record<FormLocale, string> = { en: 'en-US', es: 'es' };

// Same reason as the time-zone labels: an `Intl` formatter is expensive to build, and these
// would otherwise be rebuilt on every render.
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function cachedFormatter(key: string, build: () => Intl.DateTimeFormat) {
  const cached = formatterCache.get(key);
  if (cached) return cached;

  const formatter = build();
  formatterCache.set(key, formatter);

  return formatter;
}

const intlLocale = (locale: FormLocale) => INTL_LOCALES[locale] ?? INTL_LOCALES.en;

/**
 * `2026-08-13` reads as `Thursday, August 13`. The date is already the visitor's own day, so it
 * is formatted as a plain calendar date — pinned to UTC so the runtime's own zone can never
 * shift it to the day before.
 */
export const formatDayLabel = (locale: FormLocale, date: string) =>
  cachedFormatter(
    `day-${locale}`,
    () =>
      new Intl.DateTimeFormat(intlLocale(locale), {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      })
  ).format(new Date(`${date}T00:00:00Z`));

export const formatMonthLabel = (locale: FormLocale, month: string) =>
  cachedFormatter(
    `month-${locale}`,
    () =>
      new Intl.DateTimeFormat(intlLocale(locale), {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      })
  ).format(new Date(`${month}-01T00:00:00Z`));

/** The one place the picked date, time and zone are phrased, so both steps read alike. */
export const formatScheduleSummary = (
  locale: FormLocale,
  date: string,
  time: string,
  timeZone: string
) =>
  textFor(locale, 'summary')
    .replace('{day}', formatDayLabel(locale, date))
    .replace('{time}', time)
    .replace('{zone}', timeZoneLabel(timeZone));

export const shiftMonth = (month: string, offset: number) =>
  dateTime(`${month}-01`).add(offset, 'month').format(MONTH_FORMAT);

export const monthOf = (date: Date) => dateTime(date).format(MONTH_FORMAT);

/** Weekday the 1st of `month` falls on, 0 for Sunday — the blank tiles the grid opens with. */
export const firstWeekdayOfMonth = (month: string) => dateTime(`${month}-01`).day();

export { dateTime };
